import '../operations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Phone } from 'lucide-react';
import { useState } from 'react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { voiceProvidersApi } from '../../api/voiceProviders';
import type { VoiceProviderRequest } from '../../api/voiceProviders';
import type { VoiceProvider } from '../../types';

const emptyForm: VoiceProviderRequest = {
  name: '',
  provider_type: 'twilio',
  account_sid: '',
  auth_token: '',
  api_key: '',
  api_secret: '',
  from_number: '',
  script_template: 'Hello {{.FirstName}}, this is a security awareness test. Please visit {{.URL}}.',
  extra_config: '',
};

function toFormState(provider: VoiceProvider | null): VoiceProviderRequest {
  if (!provider) return emptyForm;
  return {
    name: provider.name,
    provider_type: provider.provider_type,
    account_sid: provider.account_sid,
    auth_token: '',
    api_key: provider.api_key,
    api_secret: '',
    from_number: provider.from_number,
    script_template: provider.script_template,
    extra_config: provider.extra_config,
  };
}

export function VoiceProviderList() {
  const queryClient = useQueryClient();
  const [formModal, setFormModal] = useState<{ open: boolean; provider: VoiceProvider | null }>({
    open: false,
    provider: null,
  });
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; provider: VoiceProvider | null }>({
    open: false,
    provider: null,
  });
  const [form, setForm] = useState<VoiceProviderRequest>(emptyForm);

  const { data: providers, isLoading } = useQuery({
    queryKey: ['voiceProviders'],
    queryFn: async () => {
      const res = await voiceProvidersApi.getAll();
      return res.data;
    },
  });

  const saveMutation = useMutation({
    mutationFn: (payload: VoiceProviderRequest & { id?: number }) =>
      payload.id ? voiceProvidersApi.update(payload.id, payload) : voiceProvidersApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voiceProviders'] });
      setFormModal({ open: false, provider: null });
      setForm(emptyForm);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => voiceProvidersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voiceProviders'] });
      setDeleteModal({ open: false, provider: null });
    },
  });

  const openCreate = () => {
    setForm(emptyForm);
    setFormModal({ open: true, provider: null });
  };

  const openEdit = (provider: VoiceProvider) => {
    setForm(toFormState(provider));
    setFormModal({ open: true, provider });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: VoiceProviderRequest & { id?: number } = { ...form };
    if (formModal.provider) payload.id = formModal.provider.id;
    saveMutation.mutate(payload);
  };

  const needsAuthToken = form.provider_type === 'twilio' || form.provider_type === 'plivo';

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (p: VoiceProvider) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg"><Phone className="w-4 h-4 text-indigo-600" /></div>
          <p className="font-medium">{p.name}</p>
        </div>
      ),
    },
    { key: 'provider_type', header: 'Type', render: (p: VoiceProvider) => <span className="text-sm text-gray-500 capitalize">{p.provider_type}</span> },
    { key: 'from_number', header: 'From', render: (p: VoiceProvider) => <span className="text-sm text-gray-500">{p.from_number}</span> },
    {
      key: 'actions',
      header: '',
      render: (p: VoiceProvider) => (
        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={() => openEdit(p)} className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" title="Edit" aria-label="Edit"><Edit className="w-4 h-4" /></button>
          <button type="button" onClick={() => setDeleteModal({ open: true, provider: p })} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="operations-eyebrow">Simulation delivery</p>
          <h1 className="text-2xl font-bold text-gray-900">Voice Providers</h1>
          <p className="text-gray-500 mt-1">Configure TTS/voice gateways for vishing campaigns</p>
        </div>
        <Button onClick={openCreate}><Plus className="w-4 h-4 mr-2" />New Provider</Button>
      </div>
      <Card>
        {isLoading ? (
          <div className="animate-pulse space-y-4">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded" />)}</div>
        ) : (
          <Table columns={columns} data={providers ?? []} keyExtractor={(p) => p.id} emptyMessage="No voice providers configured." />
        )}
      </Card>

      <Modal isOpen={formModal.open} onClose={() => setFormModal({ open: false, provider: null })} title={formModal.provider ? 'Edit Voice Provider' : 'New Voice Provider'} size="lg">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div>
            <label className="label">Provider type</label>
            <select className="input" value={form.provider_type} onChange={(e) => setForm({ ...form, provider_type: e.target.value as VoiceProviderRequest['provider_type'] })}>
              <option value="twilio">Twilio</option>
              <option value="plivo">Plivo</option>
              <option value="custom">Custom webhook</option>
            </select>
          </div>
          <div>
            <label className="label">From number</label>
            <input type="text" className="input" value={form.from_number} onChange={(e) => setForm({ ...form, from_number: e.target.value })} required />
          </div>
          {needsAuthToken && (
            <>
              <div>
                <label className="label">Account SID / Auth ID</label>
                <input type="text" className="input" value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} />
              </div>
              <div>
                <label className="label">Auth token / Auth secret</label>
                <input type="password" className="input" value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder={formModal.provider ? 'Leave blank to keep unchanged' : ''} />
              </div>
            </>
          )}
          {form.provider_type === 'custom' && (
            <>
              <div>
                <label className="label">API key</label>
                <input type="text" className="input" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} />
              </div>
              <div>
                <label className="label">API secret</label>
                <input type="password" className="input" value={form.api_secret} onChange={(e) => setForm({ ...form, api_secret: e.target.value })} placeholder={formModal.provider ? 'Leave blank to keep unchanged' : ''} />
              </div>
            </>
          )}
          <div>
            <label className="label">Script template</label>
            <textarea className="input" rows={4} value={form.script_template} onChange={(e) => setForm({ ...form, script_template: e.target.value })} required />
            <p className="text-sm text-gray-500 mt-1">Use {'{{.FirstName}}'}, {'{{.URL}}'}, etc.</p>
          </div>
          {form.provider_type === 'custom' && (
            <div>
              <label className="label">Extra config (JSON)</label>
              <textarea className="input" rows={3} value={form.extra_config} onChange={(e) => setForm({ ...form, extra_config: e.target.value })} placeholder={`{"endpoint":"..."}`} />
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setFormModal({ open: false, provider: null })}>Cancel</Button>
            <Button type="submit" loading={saveMutation.isPending}>Save</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, provider: null })} title="Delete voice provider" size="sm">
        <p>Delete <strong>{deleteModal.provider?.name}</strong>? Campaigns using it will fail to place calls.</p>
        <div className="flex justify-end gap-3 mt-4">
          <Button type="button" variant="secondary" onClick={() => setDeleteModal({ open: false, provider: null })}>Cancel</Button>
          <Button type="button" variant="danger" onClick={() => deleteModal.provider && deleteMutation.mutate(deleteModal.provider.id)} loading={deleteMutation.isPending}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
